import type { BaseFreightRate } from '@api/useFreight/types'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { Alert, Popconfirm, Tooltip } from 'antd'
import dayjs from 'dayjs'
import React from 'react'
import type { MileageBand } from '../types'

interface EditModeFooterProps {
  selectedRate: BaseFreightRate | null
  handleCancelEdit: () => void
  saveRateChanges: () => Promise<void>
  hasDuplicateMiles: boolean
  hasInvalidMileValues: boolean
  saveLoading: boolean
  editingCopyRows: MileageBand[]
}

export function EditModeFooter({
  selectedRate,
  handleCancelEdit,
  saveRateChanges,
  hasDuplicateMiles,
  hasInvalidMileValues,
  saveLoading,
  editingCopyRows,
}: EditModeFooterProps) {
  return (
    <Alert
      message={<Texto weight='600'>Editing {selectedRate?.status === 'active' ? 'Active' : 'Scheduled'} Rate</Texto>}
      type='info'
      description={
        selectedRate?.status === 'Active'
          ? `Editing rates scheduled for ${dayjs(selectedRate?.effective_from).format('MM/DD/YYYY')}`
          : `Editing rates effective from ${dayjs(selectedRate?.effective_from).format('MM/DD/YYYY')}`
      }
      showIcon
      action={
        <Horizontal justifyContent='flex-end' gap={8} className='mb-4'>
          <GraviButton appearance='outlined' onClick={handleCancelEdit}>
            Cancel
          </GraviButton>
          <Tooltip
            title={
              hasDuplicateMiles
                ? "Cannot save with duplicate 'From Miles' values"
                : hasInvalidMileValues
                  ? "Cannot save with missing or invalid 'From' or 'To' mile values"
                  : ''
            }
          >
            <span>
              {editingCopyRows.some((row) => row.rate === 0) ? (
                <Popconfirm
                  title='Warning'
                  description='Some rates are set to $0.00. Are you sure you want to save?'
                  onConfirm={() => saveRateChanges()}
                  okText='Yes'
                  cancelText='No'
                >
                  <GraviButton
                    appearance='solid'
                    theme2
                    loading={saveLoading}
                    disabled={hasDuplicateMiles || hasInvalidMileValues}
                  >
                    Save Changes
                  </GraviButton>
                </Popconfirm>
              ) : (
                <GraviButton
                  appearance='solid'
                  theme2
                  loading={saveLoading}
                  onClick={() => saveRateChanges()}
                  disabled={hasDuplicateMiles || hasInvalidMileValues}
                >
                  Save Changes
                </GraviButton>
              )}
            </span>
          </Tooltip>
        </Horizontal>
      }
    />
  )
}
